<template>
  <div class="room-info-modal">
    <div class="room-info-header">
      {{title || description}}
    </div>
    <div v-if="summary" class="room-summary text-body-2 color-dark-grey mt-2">
      {{summary}}
    </div>
    <div class="room-main-info">
      <div class="room-main-info-item" v-for="(bed, idx) in beds" :key="idx">
        <!--TODO: fix the icon and use the font-->
        <img
            class="room-main-info-item_icon--svg"
            src="/icons/icomoon/icons-svg/icon-bed.svg"
            height="18"
            width="18"
        />
        {{createBedTitle(bed)}}
      </div>

      <div class="room-main-info-item" v-if="square_footage">
        <span class="icon-ruler"></span>
        {{square_footage}}
      </div>

      <div class="room-main-info-item" v-if="room_view">
        <span class="icon-water-sun"></span>
        {{room_view}}
      </div>

      <div class="room-main-info-item" v-if="bathtubAmenity">
        <span class="icon-bath"></span>
        {{ bathtubAmenity.name }}
      </div>

      <div class="room-main-info-item" v-if="airConditioningAmenity">
        <span class="icon-snowflake"></span>
        {{airConditioningAmenity.name}}
      </div>

      <div class="room-main-info-item" v-if="tvAmenity">
        <span class="icon icon-tv"></span>
        {{ tvAmenity.name }}
      </div>

      <div class="room-main-info-item" v-if="wifiAmenity">
        <span class="icon icon-wifi"></span>
        Internet
      </div>

    </div>

    <ul class="room-amenities mt-1">
      <li class="room-amenity" v-if="false">
        <!--NOTE: this is room_board different for each rate -->
        Includes daily Grab & Go breakfast for two guests
      </li>
      <li class="room-amenity" v-if="smokingAmenity">
        {{ smokingAmenity.name }}
      </li>
    </ul>

    <div class="room-info-header pt-3" v-if="bathroomAmenities?.length || bedroomAmenities?.length  || entertainmentAmenities?.length  || foodAndDrinkAmenities?.length  || internetAmenities?.length || otherAmenities?.length">
      Room facilities
    </div>

    <section class="room-info-section" v-if="bathroomAmenities?.length">
      <div class="room-info-sub-header">
        <span class="room-info-sub-header_icon icon-bath"></span>
        Bathroom
      </div>
      <ul class="room-amenities">
        <li
            class="room-amenity"
            v-for="(amenity, idx) in bathroomAmenities"
            :key="idx">
          {{amenity.name}}
        </li>
      </ul>
    </section>

    <section class="room-info-section" v-if="bedroomAmenities?.length">
      <div class="room-info-sub-header">
        <span class="room-info-sub-header_icon icon-sofa"></span>
        Bedroom
      </div>
      <ul class="room-amenities">
        <li
            class="room-amenity"
            v-for="(amenity, idx) in bedroomAmenities"
            :key="idx">
          {{amenity.name}}
        </li>
      </ul>
    </section>

    <section class="room-info-section" v-if="entertainmentAmenities?.length">
      <div class="room-info-sub-header">
        <!--TODO: fix the icon and use the font-->
        <img
            class="room-main-info-item_icon--svg"
            src="/icons/icomoon/icons-svg/icon-film.svg"
            height="22"
            width="22"
        />
        Entertainment
      </div>
      <ul class="room-amenities">
        <li
            class="room-amenity"
            v-for="(amenity, idx) in entertainmentAmenities"
            :key="idx">
          {{amenity.name}}
        </li>
      </ul>
    </section>

    <section class="room-info-section" v-if="foodAndDrinkAmenities?.length">
      <div class="room-info-sub-header">
        <!--TODO: fix the icon and use the font-->
        <img
            class="room-info-sub-header_icon room-info-sub-header_icon--svg"
            src="/icons/icomoon/icons-svg/icon-tea-cup.svg"
            height="22"
            width="22"
        />
        Food and drink
      </div>
      <ul class="room-amenities">
        <li
            class="room-amenity"
            v-for="(amenity, idx) in foodAndDrinkAmenities"
            :key="idx">
          {{amenity.name}}
        </li>
      </ul>
    </section>

    <section class="room-info-section" v-if="internetAmenities?.length">
      <div class="room-info-sub-header">
        <span class="room-info-sub-header_icon icon-wifi"></span>
        Internet
      </div>
      <ul class="room-amenities">
        <li
            class="room-amenity"
            v-for="(amenity, idx) in internetAmenities"
            :key="idx">
          {{amenity.name}}
        </li>
      </ul>
    </section>

    <section
        class="room-info-section room-info-section--last"
        :class="{'room-info-section--long': isAmenitiesListTooLong(otherAmenities)}"
        v-if="otherAmenities?.length">
      <div class="room-info-sub-header">
        <span class="room-info-sub-header_icon icon-more"></span>
        More
      </div>

      <ul class="room-amenities">
        <li
            class="room-amenity"
            v-for="(amenity, idx) in otherAmenities"
            :key="idx">
          {{amenity.name}}
        </li>
      </ul>
    </section>

  </div>
</template>

<script setup lang="ts">
import { usePropertyStore } from '~/store/property'

const _props = defineProps({
  room: {
    type: Object,
    required: true,
  },
});

//data
const { room }  = _props;
const property = usePropertyStore().property
const { title, description, summary, amenities, beds, square_footage, room_view, smoking } = room;
const BATH_AMENITY_CODES = [142, 13, 50]
const BED_AMENITY_CODES = [2]
const ENTERTAINMENT_AMENITY_CODES = [251, 210]
const FOOD_AND_DRINK_AMENITY_CODES = [19]
const INTERNET_AMENITY_CODES = [123, 251, 54]
const TV_AMENITY_CODES = [210, 251]
const WIFI_AMENITY_CODE = 123
const SMOKING_AMENITY_CODE = 101


//computed
//TODO: find a way to group amenities by type
const bathroomAmenities = computed(() => amenities.filter((amenity: any) => BATH_AMENITY_CODES.includes(Number(amenity.ota_code))));
const bedroomAmenities = computed(() => amenities.filter((amenity: any) => BED_AMENITY_CODES.includes(Number(amenity.ota_code))));
const entertainmentAmenities = computed(() => amenities.filter((amenity: any) => ENTERTAINMENT_AMENITY_CODES.includes(Number(amenity.ota_code))));
const foodAndDrinkAmenities = computed(() => amenities.filter((amenity: any) => FOOD_AND_DRINK_AMENITY_CODES.includes(Number(amenity.ota_code))));
const internetAmenities = computed(() => amenities.filter((amenity: any) => INTERNET_AMENITY_CODES.includes(Number(amenity.ota_code))));
const otherAmenities = computed(() => {

  const categorizedAmenities = [
    ...bathroomAmenities.value,
    ...bedroomAmenities.value,
    ...entertainmentAmenities.value,
    ...foodAndDrinkAmenities.value,
    ...internetAmenities.value
  ].map((amenity: any) => Number(amenity.ota_code));

  const categorizedCodes = new Set(categorizedAmenities);

  return amenities.filter((amenity: any) =>
      !categorizedCodes.has(Number(amenity.ota_code)) || !amenity.ota_code
  );
});
//main amenities
const bathtubAmenity = computed(() => amenities.find((amenity: any) => Number(amenity.ota_code) === 13))
const tvAmenity = computed(() => amenities.find((amenity: any) => Number(amenity.ota_code) === 251))
const airConditioningAmenity = computed(() => amenities.find((amenity: any) => Number(amenity.ota_code) === 2))
const wifiAmenity = computed(() => amenities.find((amenity: any) => Number(amenity.ota_code) === WIFI_AMENITY_CODE))
const smokingAmenity = computed(() => {
  return amenities.find((amenity: any) => Number(amenity.ota_code) === SMOKING_AMENITY_CODE) || smoking
})

//methods
function createBedTitle(bed: any) {
  const { number, title } = bed;
  return `${number} ${title} Bed${number > 1 ? 's' : ''}`;
}

function isAmenitiesListTooLong(amenities: any[]) {
  return amenities.length > 12;
}

</script>

<style scoped lang="scss">
$blackColorFilter: brightness(0) saturate(100%) invert(18%) sepia(0%) saturate(1189%) hue-rotate(154deg) brightness(92%) contrast(101%); //#292929
$commonPadding: 8px;

.room-info-modal {
  color: var(--black);
}
.room-summary {
  line-height: 1.5;
}
.room-info-header {
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  text-align: left;
}
.room-info-sub-header {
  display: flex;
  gap: $commonPadding;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  text-align: left;

  @media screen and (max-width: 768px) {
    padding-bottom: 2px;
  }

  &_icon {
    font-size: 22px;
    &--svg {
      filter: $blackColorFilter;
    }
  }
}

.room-main-info {
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  max-height: 96px;
  margin: $commonPadding 0;

  @media screen and (max-width: 768px) {
    max-height: 128px;
  }
}

.room-main-info-item {
  display: flex;
  gap: $commonPadding;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  max-width: 30%;
  line-height: 2;

  @media screen and (max-width: 768px) {
    max-width: 50%;
  }

  &_icon {
    &--svg {
      filter: $blackColorFilter;
    }
  }
}

.room-info-section {
  border-bottom: 1px solid var(--grey-border);
  padding: $commonPadding 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: $commonPadding;

  @media screen and (max-width: 768px) {
    margin-top: 12px;
  }

  &--last {
    border-bottom: none;
  }

  &--long {
    .room-amenities {
      max-height: fit-content;
    }
  }
}

.room-amenities {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  max-height: 136px;

  @media screen and (max-width: 768px) {
    max-height: fit-content;
  }
}

.room-amenity {
  $bulletSpaceWidth: 1rem;
  font-size: 16px;
  font-weight: 400;
  text-align: left;
  line-height: 2.1;
  margin-left: 1.5rem;

  list-style-type: none; /* Remove default bullet point */

  @media screen and (max-width: 768px) {
    line-height: 24px;
    padding: 4px 0;
  }

  &::before {
    content: "•";
    font-size: 16px;
    display: inline-block;
    width: $bulletSpaceWidth;
    margin-left: -$bulletSpaceWidth;
  }
}

</style>